import queryString from 'query-string'
import { UserData } from '../appState'

// Used in main app
export interface BooliResponse {
  area: string
  comparable_usage: number
  electricity_price: number[]
  forecast: number[]
  gas_savings: number
  heat_price: number[]
  heating: number[]
  heating_total: number
  total_consumption: number
  household: Household[]
  production: number[] // solar production
  suggestions: Suggestion[]
  yearly_price: number
  yearly_price_heating: number
  yearly_price_per_mounth: number
}

export interface Household {
  label: string
  usage: number
}

export interface Suggestion {
  cost: number
  description: string
  id: string
  key: string
  payback_years: number
  price_per_kWh: number
  subtitle: string
  title: string
  value: string
  yearly_savings: number
  yearly_savings_kWh: number
}

type FetchSuggestionsResponse =
  | { successful: true; data: BooliResponse }
  | { successful: false; data: null }

interface ReducedUserData extends Omit<UserData, 'solar_panel_power' | 'heat_source'> {}

function isUserData(user: UserData | ReducedUserData): user is UserData {
  return (user as UserData).heat_source !== undefined
}

export function transformHeatSourceArray(userHeatSources: string[]): string[] {
  // Remove 'heating-pump-ground-rock' from the array if it exists
  const booliAPIHeatSources = userHeatSources.filter((item) => item !== 'heating-pump-ground-rock')
  // Check if the original array contains 'heating-pump-ground-rock' or 'heating-pump-ground'
  // and ensure 'heating-pump-ground' is added only once
  if (
    userHeatSources.includes('heating-pump-ground-rock') ||
    userHeatSources.includes('heating-pump-ground')
  ) {
    // Add 'heating-pump-ground' if it's not already in the result
    if (!booliAPIHeatSources.includes('heating-pump-ground')) {
      booliAPIHeatSources.push('heating-pump-ground')
    }
  }
  return booliAPIHeatSources
}

type HeatSource =
  | 'heating-pump-ground'
  | 'heating-pump-water'
  | 'wood-chips'
  | 'heating-pump-exhaust-air'
  | 'electricity-water'
  | 'district'
  | 'heating-pump-air'
  | 'electricity-direct'
  | 'fossil'

function getMostImactfulHeatSource(heatSources: HeatSource[]) {
  if (heatSources.length === 1) return heatSources[0]
  const heatSourcesInDecliningOrderOfImpact: HeatSource[] = [
    'heating-pump-ground',
    'heating-pump-water',
    'wood-chips',
    'heating-pump-exhaust-air',
    'electricity-water',
    'district',
    'heating-pump-air',
    'electricity-direct',
    'fossil',
  ]
  for (const source of heatSourcesInDecliningOrderOfImpact) {
    if (heatSources.includes(source)) {
      return source as HeatSource // Returns the first (most impactful) match found
    }
  }
}

function filterUserData(userData: UserData | ReducedUserData) {
  return Object.fromEntries(Object.entries(userData).filter(([, value]) => value !== ''))
}

function getProcessedAndFilteredUserData(userData: UserData | ReducedUserData) {
  if (!isUserData(userData)) return filterUserData(userData)
  const booliAPIHeatSources = transformHeatSourceArray(userData.heat_source)
  const mostImpactfulHeatSource = getMostImactfulHeatSource(booliAPIHeatSources as HeatSource[])
  const processedUserData = { ...userData, heat_source: mostImpactfulHeatSource }
  return filterUserData(processedUserData)
}

export async function fetchSuggestions(
  userData: UserData | ReducedUserData,
): Promise<FetchSuggestionsResponse> {
  try {
    if (!userData.living_area || !userData.postal_code || !userData.construction_year)
      throw new Error('Missing living_area, construction_year or postal_code')
    const filteredAndProcessedUserData = getProcessedAndFilteredUserData(userData)
    const queryParams = queryString.stringify(filteredAndProcessedUserData)
    const response = await fetch(
      `/kilowatt-calculation-bff/kilowatt/predict_consumption?${queryParams}`,
    )
    if (!response.ok) throw new Error('Response not ok')
    const data: BooliResponse = (await response.json()) as BooliResponse
    return {
      successful: true,
      data,
    }
  } catch (err) {
    return {
      successful: false,
      data: null,
    }
  }
}

export async function fetchIsValidPostalCode(postalCode: number | string): Promise<
  | {
      successful: true
      isValid: boolean
    }
  | {
      successful: false
    }
> {
  try {
    const URL = `/kilowatt-calculation-bff/kilowatt/validate_postal_code?postal_code=${postalCode}`
    const response = await fetch(URL)
    if (!response.ok) throw new Error('Response not ok')
    const isValid = (await response.json()) as boolean
    return {
      successful: true,
      isValid,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

// Used in InterestRateApp
const mode = import.meta.env.MODE
const apiBaseURL = mode === 'production' ? '/api' : ''
export interface InterestRateObj {
  interestPeriod: number
  interestRate: string
  interestRateCreated: string
  referenceId: string
  termsEndDate: string
  termsEndDateCreated: string
}

interface InterestList {
  listInterests: InterestRateObj[]
}

export async function fetchThreeMonthMortgageRates(): Promise<
  | {
      successful: true
      threeMonthInterestRate: number
    }
  | {
      successful: false
    }
> {
  const url = `${apiBaseURL}/interest-mortgage-service/api/v1/interest`

  try {
    const response = await fetch(url)
    const data = (await response.json()) as InterestList
    const { listInterests } = data
    if (!listInterests) throw new Error('Unable to find interest rates info')
    const threeMonthInterestRateObj = listInterests.find(
      (interest) => interest.interestPeriod === 3,
    )
    if (!threeMonthInterestRateObj) throw new Error('Unable to find 3 month interest rate')
    const threeMonthInterestRate = +threeMonthInterestRateObj.interestRate
    return {
      successful: true,
      threeMonthInterestRate,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

export interface MortgageTerms {
  maxTotalDebtForResidence: number
  minLivingSpaceForCondo: number
  maxLivingSpaceForCondo: number
  minLivingSpaceForHouse: number
  maxLivingSpaceForHouse: number
  minMaritalStatus: number
  maxMaritalStatus: number
  maxLoanToValueRatioMortgage: number
  maxLoanToValueRatioMortgageAndPersonalLoan: number
  maxLoanAmountForSecondaryHouse: number
  minLoanAmountMortgage: number
  maxLoanAmountMortgage: number
  maxLoanAmountMortgageAndPersonalLoan: number
  minDeposit: number
  maxDeposit: number
  minHousePrice: number
  minLoanAmountForIncreasedMortgage: number
  minDownPayment: number
  minAgeForLoan: number
  retirementAgeLimit: number
}

export async function fetchMortgageTerms(): Promise<
  | {
      successful: true
      data: MortgageTerms
    }
  | {
      successful: false
    }
> {
  try {
    const response = await fetch(
      'https://api.sbab.se/api/product-terms/3.0/product/loan-application',
    )
    const data = (await response.json()) as MortgageTerms
    return {
      successful: true,
      data,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

interface PriceData {
  priceDifference: string
  priceComponents: [
    {
      type: 'fixed'
      priceDifference: string
    },
  ]
  productOfferId: string
  priceDifferenceSpecification: {
    description: string
    priceDifferenceItems: [
      {
        priceDifference: string
        description: string
        calculationName: 'PriceMatrixCalculation'
        type: 'fixed'
      },
      {
        priceDifference: string
        description: string
        calculationName: 'KilowattLoanCalculation'
        type: 'fixed'
      },
    ]
    limited: boolean
  }
}

interface EffectiveInterest {
  effectiveRate: string
}

// Price diff + effective interest
export async function fetchDataForResultSection(
  marketValue: number,
  mortgageAmount: number,
  threeMonthInterestRate: number,
): Promise<
  | {
      successful: true
      priceMatrixDifference: number
      kilowattLoanDifference: number
      effectiveInterest: number
    }
  | {
      successful: false
    }
> {
  try {
    const response = await fetch(
      `${apiBaseURL}/interest-service/api/v1/interest/difference/market/mortgage/kilowatt`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketValue,
          mortgageAmount,
          brokerCodes: ['690001'],
        }),
      },
    )
    const data = (await response.json()) as PriceData
    const priceMatrixDifference = data.priceDifferenceSpecification.priceDifferenceItems.find(
      (item) => item.calculationName === 'PriceMatrixCalculation',
    )?.priceDifference
    if (!priceMatrixDifference) throw new Error('Unable to find price matrix difference')
    const kilowattLoanDifference = data.priceDifferenceSpecification.priceDifferenceItems.find(
      (item) => item.calculationName === 'KilowattLoanCalculation',
    )?.priceDifference
    if (!kilowattLoanDifference) throw new Error('Unable to find price matrix difference')
    const effectiveRateResponse = await fetch(
      `${apiBaseURL}/interest-calculation-service/api/v1/effective-interest/mortgage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rate: Math.max(0, threeMonthInterestRate + +priceMatrixDifference),
          mortgageAmount: mortgageAmount,
        }),
      },
    )
    const effectiveInterestData = (await effectiveRateResponse.json()) as EffectiveInterest
    const effectiveInterest = +effectiveInterestData.effectiveRate
    return {
      successful: true,
      priceMatrixDifference: +priceMatrixDifference,
      kilowattLoanDifference: +kilowattLoanDifference,
      effectiveInterest,
    }
  } catch (err) {
    return {
      successful: false,
    }
  }
}

// Used in EffectiveRateAccordionSection
export interface TotalCostData {
  totalPayment: number
  totalInterestPayment: number
}

export async function fetchTotalCost(rate: number, loanAmount: number) {
  try {
    const url = `${apiBaseURL}/interest-calculation-service/api/v2/total-cost/simplified`
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rate,
        amount: loanAmount,
        years: 40,
      }),
    }
    const response = await fetch(url, options)
    const data = (await response.json()) as TotalCostData
    return {
      successful: true,
      data,
    }
  } catch (err) {
    return {
      successful: false,
      data: null,
    }
  }
}
